import {
  apiServices,
  createUserVisibleError,
  deleteToken,
  setToken,
} from "../../../../core/utilities/ApiServices";
import { getCurrentRealCoordinates } from "../../../../core/utilities/locationStorage";
import {
  hasAccountRecoveryIntent,
  saveAccountRecoveryToken,
} from "../../../../core/utilities/accountRecovery";
import { AuthLoginResult } from "../../domain/entities/AuthLoginResult";

export interface AuthDataSource {
  login(
    otp: string,
    type: string,
    emailOrPhone: string,
    isLogin: boolean,
    password?: string,
  ): Promise<AuthLoginResult>;
  loginGmail(token: string): Promise<AuthLoginResult>;
  logout(): Promise<void>;
  renew(): Promise<void>;
  sendOtp(email: string, type: string): Promise<void>;
  setPassword(password: string): Promise<void>;
  removeAccount(otp: string): Promise<void>;
  requestRemoveAccount(): Promise<void>;
}

export class AuthDataSourceImpl implements AuthDataSource {
  private acceptLoginResponse(response: any): AuthLoginResult {
    if (response?.data?.recovery_required === true) {
      const recoveryToken = String(response.data.recovery_token || "").trim();
      if (!recoveryToken) throw new Error("LOGIN_FAILED");
      localStorage.removeItem("userToken");
      deleteToken();
      saveAccountRecoveryToken(recoveryToken);
      setToken(recoveryToken);
      return {
        kind: "recovery",
        missingItems: Array.isArray(response.data.missing_items)
          ? response.data.missing_items
          : [],
      };
    }

    const token = String(response?.data?.token || "").trim();
    if (!token) throw new Error("LOGIN_FAILED");
    localStorage.setItem("userToken", token);
    setToken(token);
    return {
      kind: "authenticated",
      statusOnboarding: response.data.status_onboarding,
    };
  }
  private extractErrorMessage(data: any): string {
    if (!data) return "";
    if (typeof data === "string") return data;
    if (typeof data?.message === "string") return data.message;
    if (Array.isArray(data?.message) && data.message.length > 0) {
      return String(data.message[0]);
    }
    return "";
  }
  private throwHttpError(error: any, fallbackMessage: string): never {
    const message = this.extractErrorMessage(error?.response?.data);
    if (message) {
      throw new Error(message);
    }
    if (error?.response?.status) {
      throw new Error(String(error.response.status));
    }
    throw new Error(fallbackMessage);
  }
  private throwLoginError(error: any): never {
    const response = error?.response ?? error;
    const message = this.extractErrorMessage(response?.data);

    if (response?.status === 400 && message.includes("PASSWORD_NOT_SET")) {
      throw new Error("PASSWORD_NOT_SET");
    }
    if (
      response?.status === 401 ||
      (response?.status === 400 &&
        message.toLowerCase().includes("invalid email or password"))
    ) {
      throw new Error("INVALID_AUTH");
    }
    if (response?.status === 403) {
      throw new Error("403");
    }
    throw new Error(message || "LOGIN_FAILED");
  }
  async loginGmail(token: string): Promise<AuthLoginResult> {
    let data = {
      encrypt_google_access: token,
      type: "google",
      account_recovery_intent: hasAccountRecoveryIntent() ? "true" : "",
    };
    let payload: { [key: string]: string } = {};

    Object.entries(data).forEach(([key, value]: [string, any]) => {
      if (value === null || value === "") return;
      payload[key] = value;
    });
    let endpoint = "/auth/login";
    localStorage.removeItem("userToken");
    deleteToken();
    const response = await apiServices.post(endpoint, payload);
    if (response.status === 200) {
      return this.acceptLoginResponse(response);
    } else if (response.status === 403) {
      throw new Error("403");
    } else {
      throw new Error();
    }
  }
  async logout(): Promise<void> {
    await apiServices.get(`auth/logout`);
    localStorage.clear();
    return;
  }
  async sendOtp(email: string, type: string): Promise<void> {
    let params = {};
    if (type === "0") {
      params = { email };
    } else {
      params = { phone_number: email };
    }
    try {
      const response = await apiServices.post(
        `auth/send-otp`,
        {},
        {
          params: params,
        },
      );
      if (response.status === 200) {
        return;
      }
      throw new Error();
    } catch (error) {
      const message =
        this.extractErrorMessage((error as any)?.response?.data) ||
        "Could not send the verification code right now.";
      throw createUserVisibleError(message);
    }
  }
  async login(
    otp: string,
    type: string,
    emailOrPhone: string,
    isLogin?: boolean,
    password?: string,
  ): Promise<AuthLoginResult> {
    let data = {
      otp,
      type: "local",
      encrypt_google_access: "",
      password: password ?? "",
      account_recovery_intent: hasAccountRecoveryIntent() ? "true" : "",
    };
    let payload: { [key: string]: string } = {};
    if (type === "0") {
      payload["email"] = emailOrPhone;
    } else {
      payload["phone_number"] = emailOrPhone;
    }
    Object.entries(data).forEach(([key, value]: [string, any]) => {
      if (value === null || value === "") return;
      payload[key] = value;
    });
    if (isLogin) {
      localStorage.removeItem("userToken");
      deleteToken();
    }
    let endpoint = "/auth/login";
    let response;
    try {
      response = await apiServices.post(endpoint, payload);
    } catch (error) {
      this.throwLoginError(error);
    }
    if (response.status === 200) {
      return this.acceptLoginResponse(response);
    }
    this.throwLoginError(response);
  }
  async renew(): Promise<void> {
    const token = localStorage.getItem("userToken");
    const { lat: country_lat, lng: country_lng } = getCurrentRealCoordinates();
    setToken(token ?? "");
    const response = await apiServices.get(`/auth/renew-token`, {
      params: {
        country_lat: country_lat,
        country_lng: country_lng,
      },
    });
    localStorage.setItem("userToken", response.data.token);
    setToken(response.data.token);
    if (response.status === 200) {
      return response.data.status_login;
    } else {
      throw new Error();
    }
  }
  async removeAccount(otp: string): Promise<void> {
    let endpoint = "/auth/account";
    try {
      const response = await apiServices.delete(endpoint, {
        params: { otp },
      });
      if (response.status === 200) {
        return;
      }
      throw new Error("REMOVE_ACCOUNT_FAILED");
    } catch (error) {
      this.throwHttpError(error, "REMOVE_ACCOUNT_FAILED");
    }
  }
  async setPassword(password: string): Promise<void> {
    try {
      const response = await apiServices.post("/auth/set-password", {
        password,
      });
      if (response.status === 200) {
        return;
      }
      throw new Error("SET_PASSWORD_FAILED");
    } catch (error) {
      if ((error as any)?.response?.status === 401) {
        throw new Error("401");
      }
      const message =
        this.extractErrorMessage((error as any)?.response?.data) ||
        "Could not update your password right now. Please try again.";
      throw createUserVisibleError(message);
    }
  }
  async requestRemoveAccount(): Promise<void> {
    let endpoint = "/auth/request-remove-account";
    try {
      const response = await apiServices.get(endpoint);
      if (response.status === 200) {
        return;
      }
      throw new Error("REQUEST_REMOVE_ACCOUNT_FAILED");
    } catch (error) {
      this.throwHttpError(error, "REQUEST_REMOVE_ACCOUNT_FAILED");
    }
  }
}
